import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, CheckCircle2, ArrowRightCircle, UserPlus, IndianRupee } from "lucide-react";
import { api } from "@/lib/api";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "" };

export default function RFQs() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [rfqs, setRfqs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rfqRes, vendorRes, prRes] = await Promise.all([
        api.get("/procurement/rfqs"), api.get("/procurement/vendors"), api.get("/procurement/requisitions").catch(() => ({ data: [] })),
      ]);
      setRfqs(rfqRes.data);
      setVendors(vendorRes.data);
      setRequisitions(prRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  const send = async (rfq) => {
    setBusyId(rfq.id);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/send`);
      toast.success("RFQ sent");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const select = async (rfq, vendorId) => {
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${vendorId}/select`);
      toast.success("Vendor selected");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const convert = async (rfq) => {
    const selected = rfq.invited_vendors.find((v) => v.is_selected);
    if (!selected) { toast.error("Select a vendor first"); return; }
    setBusyId(rfq.id);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/convert-to-po`, { vendor_id: selected.vendor_id, po_date: new Date().toISOString().slice(0, 10) });
      toast.success("Converted to Purchase Order");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="rfqs-page">
      <PageHeader title="RFQs" description="Request quotations from invited vendors, record their quotes, then convert the best one into a PO." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateRFQDialog pfProjects={pf.projects} companies={companies} vendors={vendors} onCreated={load} />}</div>} />
      {!loading && rfqs.length === 0 ? (
        <EmptyState title="No RFQs yet" testId="rfqs-empty-state" />
      ) : (
        <div className="space-y-3">
          {pf.apply(rfqs, (r) => r.project_id).map((rfq) => (
            <div key={rfq.id} className="rounded-xl border border-border bg-card p-4" data-testid={`rfq-card-${rfq.number}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{rfq.number}</p>
                  <p className="text-sm text-foreground">{rfq.rfq_date}{rfq.due_date ? ` · due ${rfq.due_date}` : ""}</p>
                  {rfq.requisition_id && (
                    <p className="mt-0.5 text-[11px] text-sky-600 dark:text-sky-400">From requisition {requisitions.find((r) => r.id === rfq.requisition_id)?.number || "—"}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={rfq.status} />
                  {canWrite && <DeleteDocButton path={`/procurement/rfqs/${rfq.id}`} label="RFQ" number={rfq.number} status={rfq.status} allowed={["DRAFT", "SENT", "CLOSED", "CANCELLED"]} onDeleted={load} />}
                  {canWrite && !["CONVERTED", "CANCELLED"].includes(rfq.status) && (
                    <InviteVendorDialog rfq={rfq} vendors={vendors} vendorName={vendorName} onInvited={load} />
                  )}
                  {canWrite && rfq.status === "DRAFT" && <Button size="sm" variant="outline" disabled={busyId === rfq.id} onClick={() => send(rfq)} data-testid={`send-rfq-button-${rfq.number}`}><Send className="h-3.5 w-3.5" /> Send</Button>}
                  {canWrite && rfq.status === "SENT" && <Button size="sm" variant="outline" disabled={busyId === rfq.id} onClick={() => convert(rfq)} data-testid={`convert-rfq-button-${rfq.number}`}><ArrowRightCircle className="h-3.5 w-3.5" /> Convert to PO</Button>}
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">{rfq.items.map((it) => <li key={it.id}>{it.description} — {it.quantity}{it.estimated_rate ? ` · estimated ₹${it.estimated_rate}` : ""}</li>)}</ul>
              <div className="mt-3 space-y-1.5" data-testid={`rfq-vendors-${rfq.number}`}>
                {rfq.invited_vendors.map((iv) => {
                  const quotes = rfq.invited_vendors.filter((v) => v.quoted_amount != null).map((v) => Number(v.quoted_amount));
                  const lowest = quotes.length > 1 && iv.quoted_amount != null && Number(iv.quoted_amount) === Math.min(...quotes);
                  return (
                  <div key={iv.id} className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs">
                    <span className="flex-1 text-foreground">
                      {vendorName(iv.vendor_id)}
                      {lowest && <span className="ml-2 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">Lowest</span>}
                    </span>
                    <span className="font-mono text-foreground">
                      {iv.quoted_amount != null ? `₹${Number(iv.quoted_amount).toLocaleString("en-IN")}` : <span className="text-muted-foreground">No quote</span>}
                      {iv.quoted_amount != null && rfq.items.length > 1 && !iv.line_rates && <span className="ml-1 text-amber-600 dark:text-amber-400">(total only)</span>}
                    </span>
                    {canWrite && !["CONVERTED", "CANCELLED"].includes(rfq.status) && (
                      <QuoteDialog rfq={rfq} invite={iv} vendorName={vendorName(iv.vendor_id)} onSaved={load} />
                    )}
                    {canWrite && (
                      <Button size="sm" variant={iv.is_selected ? "default" : "outline"} onClick={() => select(rfq, iv.vendor_id)} data-testid={`select-rfq-vendor-button-${rfq.number}-${iv.vendor_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> {iv.is_selected ? "Selected" : "Select"}
                      </Button>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteVendorDialog({ rfq, vendors, vendorName, onInvited }) {
  const [open, setOpen] = useState(false);
  const [vendorIds, setVendorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const invitedIds = new Set(rfq.invited_vendors.map((v) => v.vendor_id));
  const available = (vendors || []).filter((v) => !invitedIds.has(v.id));
  const toggleVendor = (id) => setVendorIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const submit = async () => {
    if (vendorIds.length === 0) { toast.error("Select at least one vendor"); return; }
    setSaving(true);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors`, { vendor_ids: vendorIds });
      toast.success("Vendor(s) invited");
      setVendorIds([]);
      setOpen(false);
      onInvited?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`invite-vendor-button-${rfq.number}`}><UserPlus className="h-3.5 w-3.5" /> Invite Vendor</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Invite vendors to {rfq.number}</DialogTitle></DialogHeader>
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`invite-vendor-empty-${rfq.number}`}>All vendors have already been invited to this RFQ.</p>
        ) : (
          <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-border p-2">
            {available.map((v) => (
              <label key={v.id} className="flex items-center gap-2 text-sm text-foreground" data-testid={`invite-vendor-checkbox-${rfq.number}-${v.code}`}>
                <Checkbox checked={vendorIds.includes(v.id)} onCheckedChange={() => toggleVendor(v.id)} /> {v.name}
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button disabled={saving || available.length === 0} onClick={submit} data-testid={`invite-vendor-submit-button-${rfq.number}`}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRFQDialog({ companies, pfProjects, vendors, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", rfq_date: "", due_date: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [vendorIds, setVendorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));
  const toggleVendor = (id) => setVendorIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/rfqs", { ...form, project_id: form.project_id || null, due_date: form.due_date || null, items, vendor_ids: vendorIds });
      toast.success("RFQ created");
      setForm({ company_id: "", rfq_date: "", due_date: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setVendorIds([]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-rfq-button"><Plus className="h-4 w-4" /> New RFQ</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New RFQ</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-rfq-form">
          <div>
            <Label>Project</Label>
            <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="rfqs-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="rfq-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="rfq-date-input" value={form.rfq_date} onChange={set("rfq_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`rfq-item-row-${idx}`}>
                  <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`rfq-item-desc-input-${idx}`} />
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`rfq-item-qty-input-${idx}`} />
                  {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-rfq-item-button-${idx}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-rfq-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div>
            <Label>Invite vendors</Label>
            <div className="mt-1.5 space-y-1.5 max-h-32 overflow-y-auto rounded-lg border border-border p-2">
              {(vendors || []).map((v) => (
                <label key={v.id} className="flex items-center gap-2 text-sm text-foreground" data-testid={`rfq-invite-vendor-checkbox-${v.code}`}>
                  <Checkbox checked={vendorIds.includes(v.id)} onCheckedChange={() => toggleVendor(v.id)} /> {v.name}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-rfq-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDialog({ rfq, invite, vendorName, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState({});
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const existing = invite.line_rates || {};
    const single = rfq.items.length === 1 && invite.quoted_amount != null && !invite.line_rates
      ? { [rfq.items[0].id]: String((Number(invite.quoted_amount) / Number(rfq.items[0].quantity)).toFixed(2)) }
      : {};
    setRates(Object.fromEntries(rfq.items.map((it) => [it.id, existing[it.id] != null ? String(existing[it.id]) : single[it.id] || ""])));
    setRemarks(invite.remarks || "");
  }, [open, rfq, invite]);

  const total = rfq.items.reduce((sum, it) => sum + Number(it.quantity) * Number(rates[it.id] || 0), 0);
  const complete = rfq.items.every((it) => rates[it.id] !== "" && rates[it.id] != null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${invite.vendor_id}/quote`, { line_rates: rates, remarks: remarks || null });
      toast.success("Quote recorded");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`rfq-vendor-quote-button-${rfq.number}-${invite.vendor_id}`}>
          <IndianRupee className="h-3.5 w-3.5" /> {invite.quoted_amount != null ? "Edit quote" : "Enter quote"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Quote from {vendorName}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="space-y-2">
            <Label>Rate quoted for each item</Label>
            {rfq.items.map((it) => (
              <div key={it.id} className="flex items-center gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{it.description}</div>
                  <div className="text-[11px] text-muted-foreground">Qty {it.quantity}{it.estimated_rate ? ` · estimated ₹${it.estimated_rate}` : ""}</div>
                </div>
                <Input required type="number" step="0.01" min="0" placeholder="Rate" className="w-28" value={rates[it.id] ?? ""}
                  onChange={(e) => setRates({ ...rates, [it.id]: e.target.value })} data-testid={`rfq-quote-rate-${it.id}`} />
              </div>
            ))}
          </div>
          <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Delivery time, validity, terms…" className="mt-1.5" /></div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm font-medium text-foreground">Total quote: ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !complete}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save quote</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
